/**
 * CCFS Calculation Engine — Shared between public and internal calculators
 * 
 * This file contains ALL penalty/savings math.
 * If government rates change, update ONLY this file.
 */

// FY due dates: FS (AOC-4) due ~30 Sep, AR (MGT-7) due ~29 Nov of following year
export const FY_TABLE = [
  { fy: '2017-18', fsDue: new Date(2018, 9, 30), arDue: new Date(2018, 10, 29) },
  { fy: '2018-19', fsDue: new Date(2019, 9, 30), arDue: new Date(2019, 10, 29) },
  { fy: '2019-20', fsDue: new Date(2020, 9, 30), arDue: new Date(2020, 10, 29) },
  { fy: '2020-21', fsDue: new Date(2021, 9, 30), arDue: new Date(2021, 10, 29) },
  { fy: '2021-22', fsDue: new Date(2022, 9, 30), arDue: new Date(2022, 10, 29) },
  { fy: '2022-23', fsDue: new Date(2023, 9, 30), arDue: new Date(2023, 10, 29) },
  { fy: '2023-24', fsDue: new Date(2024, 9, 30), arDue: new Date(2024, 10, 29) },
  { fy: '2024-25', fsDue: new Date(2025, 9, 30), arDue: new Date(2025, 10, 29) },
];

export const ALL_FYS = FY_TABLE.map(d => d.fy);

// Rate: ₹100 per day of delay per form
const RATE_PER_DAY = 100;
// CCFS discount: 90% waiver → pay only 10%
const CCFS_DISCOUNT = 0.10;

export function daysDiff(from, to) {
  return Math.ceil((to.getTime() - from.getTime()) / 86400000);
}

export function fmt(n) {
  return '\u20B9' + Math.round(n).toLocaleString('en-IN');
}

/**
 * Public calculator: combined FS+AR calculation per FY
 * Used by the website CCFS calculator
 */
export function calculatePublic({ lastFiledFY, intent, status, cessationFY }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const lastFYCode = lastFiledFY.replace('FY ', '');

  let pendingFYs = FY_TABLE.filter(d => d.fy > lastFYCode);

  if (intent === 'close' && status === 'closed' && cessationFY) {
    const cessFYCode = cessationFY.replace('FY ', '');
    pendingFYs = pendingFYs.filter(d => d.fy <= cessFYCode);
  }

  if (pendingFYs.length === 0) return { empty: true };

  const fyDetails = pendingFYs.map(d => {
    const fsDelay = Math.max(0, daysDiff(d.fsDue, today) + 1);
    const arDelay = Math.max(0, daysDiff(d.arDue, today));
    const totalDelay = fsDelay + arDelay;
    const normalFee = totalDelay * RATE_PER_DAY;
    const ccfsFee = Math.round(normalFee * CCFS_DISCOUNT);
    return { fy: d.fy, fsDelay, arDelay, totalDelay, normalFee, ccfsFee, saving: normalFee - ccfsFee };
  });

  return {
    fyDetails,
    totalNormal: fyDetails.reduce((s, d) => s + d.normalFee, 0),
    totalCCFS: fyDetails.reduce((s, d) => s + d.ccfsFee, 0),
    totalSaving: fyDetails.reduce((s, d) => s + d.saving, 0),
  };
}

/**
 * Internal calculator: independent FS/AR calculation per FY
 * Used by Admin and R-PACE partner workspace
 * 
 * @param {Object} filingMatrix - { '2021-22': { fs: false, ar: false }, ... }
 *   false = Not Filed (penalty applies), true = Filed (no penalty)
 */
export function calculateInternal({ filingMatrix }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const fyDetails = [];

  for (const fyRow of FY_TABLE) {
    const entry = filingMatrix[fyRow.fy];
    if (!entry) continue;

    const fsFiled = entry.fs;
    const arFiled = entry.ar;

    // Skip FYs where both are filed
    if (fsFiled && arFiled) continue;

    const fsDelay = fsFiled ? 0 : Math.max(0, daysDiff(fyRow.fsDue, today) + 1);
    const arDelay = arFiled ? 0 : Math.max(0, daysDiff(fyRow.arDue, today));

    const fsNormal = fsDelay * RATE_PER_DAY;
    const arNormal = arDelay * RATE_PER_DAY;
    const totalNormal = fsNormal + arNormal;
    const totalCCFS = Math.round(totalNormal * CCFS_DISCOUNT);

    fyDetails.push({
      fy: fyRow.fy,
      fsFiled,
      arFiled,
      fsDelay,
      arDelay,
      totalDelay: fsDelay + arDelay,
      fsNormal,
      arNormal,
      normalFee: totalNormal,
      ccfsFee: totalCCFS,
      saving: totalNormal - totalCCFS,
    });
  }

  if (fyDetails.length === 0) return { empty: true };

  return {
    fyDetails,
    totalNormal: fyDetails.reduce((s, d) => s + d.normalFee, 0),
    totalCCFS: fyDetails.reduce((s, d) => s + d.ccfsFee, 0),
    totalSaving: fyDetails.reduce((s, d) => s + d.saving, 0),
  };
}

/**
 * Build initial filing matrix from last filed year and intent
 * Years at/before lastFiled → both filed (true)
 * Years after lastFiled up to endFY → both not filed (false)
 */
export function buildFilingMatrix({ lastFiledFY, intent, status, cessationFY }) {
  const lastCode = lastFiledFY.replace('FY ', '');

  // Determine the end year based on intent
  let endFY = '2024-25';
  if (intent === 'close' && status === 'closed' && cessationFY) {
    endFY = cessationFY.replace('FY ', '');
  }

  const matrix = {};
  for (const row of FY_TABLE) {
    if (row.fy > endFY) continue;
    const isFiled = row.fy <= lastCode;
    matrix[row.fy] = { fs: isFiled, ar: isFiled };
  }
  return matrix;
}
